import { CachePolicies, useFetch } from 'use-http';
import { routes } from '@/webapi/routes';
import {
  DateRange,
  ExperiencesSummary,
  StoreSummary,
} from '@/features/dashboard/reports/models';
import { MetricKind } from '@/webapi/models';

export function useReportsApi(initialLoading = true) {
  const { post, loading } = useFetch(``, (globalOptions) => ({
    ...globalOptions,
    ...{
      credentials: `include`,
      cachePolicy: CachePolicies.NO_CACHE,
      loading: initialLoading,
    },
  }));

  async function getStoreSummary(
    dateRange: DateRange,
    startDate?: Date,
    endDate?: Date,
  ): Promise<StoreSummary> {
    const { endAt, startAt } = getRange(dateRange);
    const isCustom = dateRange === DateRange.CUSTOM;
    return post(routes.storeSummary, {
      startAt: isCustom ? startDate : startAt,
      endAt: isCustom ? endDate : endAt,
    });
  }

  async function getExperiencesSummary(
    page: number,
    size: number,
    args: { dateRange: DateRange; metric: MetricKind },
  ): Promise<ExperiencesSummary> {
    const { endAt, startAt } = getRange(args.dateRange);
    return post(routes.experienceSummary, {
      startAt,
      endAt,
      page,
      size,
      sort: args.metric,
    });
  }

  async function getExperiencesNames(
    ids: Array<string>,
  ): Promise<{ names: Map<string, string> }> {
    return post(routes.experiencesNames, {
      ids,
    });
  }

  return {
    loading,
    getStoreSummary,
    getExperiencesSummary,
    getExperiencesNames,
  };
}

export function getRange(dateRange: DateRange) {
  let endAt = truncateMinutes(new Date());
  let startAt: Date;
  const oneDay = 8.64e7;
  switch (dateRange) {
    case DateRange.LAST_7_DAYS:
      startAt = truncateMinutes(new Date(endAt.valueOf() - oneDay * 7));
      break;
    case DateRange.LAST_30_DAYS:
      startAt = truncateMinutes(new Date(endAt.valueOf() - oneDay * 30));
      break;
    case DateRange.TODAY:
      startAt = endAt;
      break;
    case DateRange.YESTERDAY:
      startAt = truncateMinutes(new Date(endAt.valueOf() - oneDay));
      startAt.setUTCHours(0, 0, 0, 0);
      endAt = startAt;
      endAt.setUTCHours(23, 59, 59, 999);
      break;
    case DateRange.LAST_YEAR:
      startAt = truncateMinutes(new Date(endAt.valueOf() - oneDay * 365));
      break;
    case DateRange.ALL_TIME:
      startAt = truncateMinutes(new Date(endAt.valueOf() - oneDay * 365 * 100));
      break;
    case DateRange.CUSTOM:
      break;
    case DateRange.LATEST:
      break;
    default:
      startAt = truncateMinutes(new Date(endAt.valueOf() - oneDay * 7));
  }
  return { endAt, startAt };
}
function truncateMinutes(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    date.getHours(),
    0,
    0,
    0,
  );
}
